// =============================================================================
// Runway — per-vendor cost + runway board (super-admin only)
//
// Reads:
//   • vendor_accounts + vendor_ledger  (Platform DB — config, balances, top-ups)
//   • per-vendor consumption           (App DB via list-tenant-costs vendor_rollup)
//
// Computes, per vendor: consumption in the selected period, daily burn, balance
// (prepaid), budget headroom (metered), renewal (subscription), and days of
// runway remaining. Sorted so whatever runs dry first floats to the top.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Modal, Spinner } from '@/components/shared/UIComponents';
import {
  listVendorAccounts, listVendorLedger, fetchVendorRollup, recordVendorPurchase, updateVendorAccount,
  type VendorAccount, type VendorLedgerEntry, type VendorRollupRow,
} from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { TenantCostMatrix } from './TenantCostMatrix';
import { VendorReconcile } from './VendorReconcile';
import { VendorSettingsModal } from './VendorSettingsModal';

type PeriodKey = 'month' | 'quarter' | 'year' | 'custom';
const PERIOD_DAYS: Record<Exclude<PeriodKey, 'custom'>, number> = { month: 30, quarter: 90, year: 365 };

const usd = (cents: number | null | undefined): string =>
  cents == null ? '—' : '$' + (Math.round(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

interface VendorView {
  a: VendorAccount;
  consumptionCents: number;
  eventCount: number;
  unpricedCount: number;
  burnPerDay: number;
  runwayDays: number | null;   // null = n/a for this shape
  headline: string;
  tone: 'danger' | 'warning' | 'success' | 'muted';
  sub: string;
  pct: number;                 // 0-100 bar
  urgency: number;             // sort key, lower = more urgent
}

function daysToRenewal(renewalDay: number | null): number | null {
  if (!renewalDay) return null;
  const now = new Date();
  const y = now.getUTCFullYear(), m = now.getUTCMonth(), d = now.getUTCDate();
  let next = new Date(Date.UTC(y, m, renewalDay));
  if (renewalDay <= d) next = new Date(Date.UTC(y, m + 1, renewalDay));
  return Math.max(0, Math.round((next.getTime() - now.getTime()) / 86400000));
}

export function RunwayPage() {
  const [tab, setTab] = useState<'runway' | 'tenants' | 'reconcile'>('runway');
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<VendorAccount[]>([]);
  const [ledger, setLedger] = useState<VendorLedgerEntry[]>([]);
  const [rollup, setRollup] = useState<VendorRollupRow[]>([]);
  const [purchaseFor, setPurchaseFor] = useState<VendorAccount | null>(null);
  const [editFor, setEditFor] = useState<VendorAccount | null>(null);

  const range = useMemo(() => {
    const end = period === 'custom' && customEnd ? new Date(customEnd) : new Date();
    const start = period === 'custom' && customStart
      ? new Date(customStart)
      : new Date(end.getTime() - (PERIOD_DAYS[period as Exclude<PeriodKey, 'custom'>] ?? 30) * 86400000);
    const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
    return { startIso: start.toISOString(), endIso: end.toISOString(), days };
  }, [period, customStart, customEnd]);

  const load = async () => {
    setLoading(true);
    try {
      const [acc, led, roll] = await Promise.all([
        listVendorAccounts(),
        listVendorLedger(),
        fetchVendorRollup(range.startIso, range.endIso),
      ]);
      setAccounts(acc); setLedger(led); setRollup(roll);
    } catch (e) {
      toast.error(errorMessage(e) || 'Failed to load runway data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.startIso, range.endIso]);

  // Consumption per vendor_key (sum across service lines).
  const consumption = useMemo(() => {
    const m = new Map<string, { cents: number; events: number; unpriced: number }>();
    for (const r of rollup) {
      const cur = m.get(r.vendor) ?? { cents: 0, events: 0, unpriced: 0 };
      cur.cents += Number(r.cost_cents) || 0;
      cur.events += Number(r.event_count) || 0;
      cur.unpriced += Number(r.unpriced_count) || 0;
      m.set(r.vendor, cur);
    }
    return m;
  }, [rollup]);

  const views: VendorView[] = useMemo(() => {
    return accounts.filter(a => a.is_active).map(a => {
      const c = consumption.get(a.vendor_key) ?? { cents: 0, events: 0, unpriced: 0 };
      const burnPerDay = c.cents / range.days;
      let runwayDays: number | null = null;
      let headline = '', sub = '', pct = 0;
      let tone: VendorView['tone'] = 'success';
      let urgency = 999;

      if (a.billing_shape === 'prepaid') {
        const bal = a.balance_cents ?? 0;
        runwayDays = burnPerDay > 0 ? Math.round(bal / burnPerDay) : null;
        headline = runwayDays == null ? usd(bal) + ' left' : `≈ ${runwayDays} days`;
        sub = `${usd(bal)} balance · ${usd(c.cents)} used`;
        pct = bal > 0 ? Math.min(100, (c.cents / (bal + c.cents)) * 100) : 100;
        urgency = runwayDays ?? 900;
        tone = runwayDays != null && runwayDays <= a.low_runway_alert_days ? 'danger'
             : runwayDays != null && runwayDays <= a.low_runway_alert_days * 2 ? 'warning' : 'success';
      } else if (a.billing_shape === 'metered') {
        const budget = a.monthly_budget_cents;
        headline = budget ? `${usd(c.cents)} / ${usd(budget)}` : usd(c.cents) + ' spent';
        sub = budget ? 'metered · vs monthly cap' : 'metered · no cap set';
        pct = budget ? Math.min(100, (c.cents / budget) * 100) : Math.min(100, c.events ? 60 : 0);
        if (budget) {
          const headroom = budget - c.cents;
          runwayDays = burnPerDay > 0 ? Math.round(headroom / burnPerDay) : null;
          tone = pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : 'success';
          urgency = runwayDays ?? 500;
        } else {
          tone = c.unpriced > 0 || !a.is_tenant_attributable ? 'warning' : 'success';
          urgency = a.is_tenant_attributable ? 400 : 5;
        }
      } else if (a.billing_shape === 'subscription' || a.billing_shape === 'build') {
        const rd = daysToRenewal(a.renewal_day);
        headline = a.billing_shape === 'build' ? usd(a.monthly_amount_cents) + '/mo · R&D'
                 : rd != null ? `renews ${rd}d` : usd(a.monthly_amount_cents) + '/mo';
        sub = a.billing_shape === 'build' ? 'excluded from cost-to-serve' : usd(a.monthly_amount_cents) + '/mo · flat sub';
        pct = rd != null ? Math.round(((30 - Math.min(30, rd)) / 30) * 100) : 40;
        tone = a.billing_shape === 'build' ? 'muted' : 'success';
        urgency = a.billing_shape === 'build' ? 950 : (rd ?? 300);
      } else { // sandbox
        headline = 'sandbox';
        sub = 'not live';
        tone = 'muted';
        pct = 0;
        urgency = 990;
      }

      return { a, consumptionCents: c.cents, eventCount: c.events, unpricedCount: c.unpriced,
               burnPerDay, runwayDays, headline, tone, sub, pct, urgency };
    }).sort((x, y) => x.urgency - y.urgency);
  }, [accounts, consumption, range.days]);

  const paidByVendor = useMemo(() => {
    const s = new Date(range.startIso).getTime(), e = new Date(range.endIso).getTime();
    const m = new Map<string, number>();
    for (const l of ledger) {
      const t = new Date(l.occurred_at).getTime();
      if (t >= s && t < e) m.set(l.vendor_key, (m.get(l.vendor_key) ?? 0) + (Number(l.amount_cents) || 0));
    }
    return m;
  }, [ledger, range]);
  const paidInPeriod = useMemo(() =>
    [...paidByVendor.values()].reduce((s, v) => s + v, 0), [paidByVendor]);

  const runtimeBurn = useMemo(() =>
    views.filter(v => v.a.category !== 'build').reduce((s, v) => s + v.consumptionCents, 0), [views]);
  const buildCost = useMemo(() =>
    views.filter(v => v.a.category === 'build').reduce((s, v) => s + (v.a.monthly_amount_cents ?? 0), 0), [views]);
  const nextDry = useMemo(() =>
    views.filter(v => v.runwayDays != null && v.a.billing_shape === 'prepaid')
      .sort((a, b) => (a.runwayDays ?? 0) - (b.runwayDays ?? 0))[0], [views]);
  const anyUnpriced = views.some(v => v.unpricedCount > 0);

  const toneCol: Record<VendorView['tone'], string> = {
    danger: 'text-red-600', warning: 'text-amber-600', success: 'text-emerald-600', muted: 'text-surface-400',
  };
  const barCol: Record<VendorView['tone'], string> = {
    danger: 'bg-red-500', warning: 'bg-amber-500', success: 'bg-emerald-500', muted: 'bg-surface-300',
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-surface-900">Vendor runway</h1>
          <p className="text-sm text-surface-500">Per-vendor spend, balance, and days until you need to top up.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} title="Refresh"
            className="inline-flex items-center justify-center rounded-lg border border-surface-200 p-2 text-surface-500 hover:bg-surface-50 hover:text-surface-900 disabled:opacity-50">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <div className="inline-flex rounded-lg border border-surface-200 overflow-hidden text-sm">
            {(['month', 'quarter', 'year', 'custom'] as PeriodKey[]).map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 capitalize ${period === p ? 'bg-surface-100 text-surface-900 font-medium' : 'text-surface-500 hover:bg-surface-50'}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {period === 'custom' && (
        <div className="flex items-center gap-2 mb-4 text-sm">
          <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="input-field py-1.5" />
          <span className="text-surface-400">to</span>
          <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="input-field py-1.5" />
        </div>
      )}

      <div className="flex gap-4 border-b border-surface-200 mb-4 text-sm">
        {(['runway', 'tenants', 'reconcile'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`pb-2 -mb-px capitalize border-b-2 ${tab === t ? 'border-brand-500 text-surface-900 font-medium' : 'border-transparent text-surface-500 hover:text-surface-700'}`}>
            {t === 'runway' ? 'Runway' : t === 'tenants' ? 'Tenants' : 'Reconcile'}
          </button>
        ))}
      </div>

      {tab === 'tenants' ? (
        <TenantCostMatrix startIso={range.startIso} endIso={range.endIso} days={range.days} />
      ) : tab === 'reconcile' ? (
        <VendorReconcile accounts={accounts} ledger={ledger} consumption={consumption}
          days={range.days} startIso={range.startIso} endIso={range.endIso} loading={loading} />
      ) : (
      <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-surface-50 rounded-lg p-3">
          <p className="text-xs text-surface-500 mb-1">Runtime spend · {range.days}d</p>
          <p className="text-xl font-medium text-surface-900">{usd(runtimeBurn)}</p>
        </div>
        <div className="bg-surface-50 rounded-lg p-3">
          <p className="text-xs text-surface-500 mb-1">Build / R&amp;D</p>
          <p className="text-xl font-medium text-surface-900">{usd(buildCost)}<span className="text-xs text-surface-400">/mo</span></p>
        </div>
        <div className="bg-surface-50 rounded-lg p-3">
          <p className="text-xs text-surface-500 mb-1">Next to run dry</p>
          <p className="text-xl font-medium text-amber-600">{nextDry ? `${nextDry.a.display_name} · ${nextDry.runwayDays}d` : '—'}</p>
        </div>
      </div>

      {anyUnpriced && (
        <div className="flex items-center gap-2 bg-amber-50 text-amber-700 rounded-lg px-3 py-2 text-sm mb-4">
          <span>Some calls have no matching pricing row — that spend is logged as $0. Check the pricing matrix.</span>
        </div>
      )}
      <div className="text-xs text-surface-500 bg-surface-50 rounded-lg px-3 py-2 mb-3 leading-relaxed">
        Each card's big number is <span className="text-surface-700">spend auto-tracked from usage logs</span> (an estimate). <span className="text-surface-700">Top up</span> / <span className="text-surface-700">Record payment</span> logs what you <span className="text-surface-700">actually paid</span> — total recorded this period: <span className="font-medium text-surface-700">{usd(paidInPeriod)}</span>. The <span className="text-surface-700">Reconcile</span> tab compares tracked vs paid.
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Spinner /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {views.map(v => (
            <div key={v.a.vendor_key} className="bg-white border border-surface-200 rounded-xl p-3.5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-surface-900">{v.a.display_name}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] px-2 py-0.5 rounded bg-surface-100 text-surface-500 capitalize">{v.a.billing_shape}</span>
                  <button onClick={() => setEditFor(v.a)} title="Vendor settings"
                    className="text-[11px] text-surface-400 hover:text-surface-700 px-1">Edit</button>
                </div>
              </div>
              <p className={`text-lg font-medium ${toneCol[v.tone]}`}>{v.headline}</p>
              <p className="text-xs text-surface-500 mb-2">{v.sub}</p>
              <div className="h-1.5 rounded bg-surface-100 overflow-hidden mb-2">
                <div className={`h-full rounded ${barCol[v.tone]}`} style={{ width: `${Math.round(v.pct)}%` }} />
              </div>
              {paidByVendor.get(v.a.vendor_key) ? (
                <p className="text-[11px] text-surface-500 mb-2">You recorded paid: <span className="font-medium text-surface-700">{usd(paidByVendor.get(v.a.vendor_key)!)}</span></p>
              ) : null}
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-surface-400">
                  {v.eventCount ? `${v.eventCount.toLocaleString()} calls` : ''}{!v.a.is_tenant_attributable ? ' · client-side' : ''}
                </span>
                {v.a.billing_shape !== 'sandbox' && (
                  <button onClick={() => setPurchaseFor(v.a)}
                    className="text-[11px] px-2 py-1 rounded border border-surface-300 text-surface-600 hover:bg-surface-50">
                    {v.a.billing_shape === 'prepaid' ? 'Top up' : 'Record payment'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {purchaseFor && (
        <PurchaseModal
          vendor={purchaseFor}
          onClose={() => setPurchaseFor(null)}
          onSaved={() => { setPurchaseFor(null); load(); }}
        />
      )}
      {editFor && (
        <VendorSettingsModal
          vendor={editFor}
          onClose={() => setEditFor(null)}
          onSaved={() => { setEditFor(null); load(); }}
        />
      )}
    </div>
  );
}

function PurchaseModal({ vendor, onClose, onSaved }: { vendor: VendorAccount; onClose: () => void; onSaved: () => void }) {
  const [amount, setAmount] = useState('');
  const [credits, setCredits] = useState('');
  const [note, setNote] = useState('');
  const [when, setWhen] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const isPrepaid = vendor.billing_shape === 'prepaid';

  const save = async () => {
    const cents = Math.round(parseFloat(amount || '0') * 100);
    if (!Number.isFinite(cents) || cents === 0) { toast.error('Enter an amount'); return; }
    setSaving(true);
    try {
      const cr = credits ? parseFloat(credits) : null;
      await recordVendorPurchase({
        vendor_key: vendor.vendor_key,
        amount_cents: cents,
        credits: cr,
        entry_type: vendor.billing_shape === 'subscription' || vendor.billing_shape === 'build' ? 'subscription'
                  : vendor.billing_shape === 'metered' ? 'invoice' : 'topup',
        occurred_at: new Date(when).toISOString(),
        note: note || null,
      });
      // Prepaid: roll the top-up into the running balance so runway updates.
      if (isPrepaid) {
        await updateVendorAccount(vendor.vendor_key, {
          balance_cents: (vendor.balance_cents ?? 0) + cents,
          credits_balance: cr != null ? (vendor.credits_balance ?? 0) + cr : vendor.credits_balance,
        });
      }
      toast.success('Recorded');
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e) || 'Failed to record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Record ${isPrepaid ? 'top-up' : 'payment'} — ${vendor.display_name}`}>
      <div className="space-y-3">
        <p className="text-xs text-surface-500 bg-surface-50 rounded-lg px-3 py-2 leading-relaxed">
          {isPrepaid
            ? 'Adds to this vendor’s prepaid balance so the runway (days left) recomputes.'
            : 'Logs what you actually paid this vendor. It shows on the card as “You recorded paid” and on the Reconcile tab vs tracked usage — it does not change the auto-tracked number.'}
        </p>
        <label className="block text-sm">
          <span className="text-surface-600">Amount paid (USD)</span>
          <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
            className="input-field mt-1 w-full" placeholder="54.00" autoFocus />
        </label>
        {isPrepaid && (
          <label className="block text-sm">
            <span className="text-surface-600">Credits added (optional)</span>
            <input type="number" value={credits} onChange={e => setCredits(e.target.value)}
              className="input-field mt-1 w-full" placeholder="2000" />
          </label>
        )}
        <label className="block text-sm">
          <span className="text-surface-600">Date</span>
          <input type="date" value={when} onChange={e => setWhen(e.target.value)} className="input-field mt-1 w-full" />
        </label>
        <label className="block text-sm">
          <span className="text-surface-600">Note (optional)</span>
          <input type="text" value={note} onChange={e => setNote(e.target.value)}
            className="input-field mt-1 w-full" placeholder="Essential plan renewal" />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-surface-600 hover:bg-surface-100">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 rounded-lg text-sm bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Record'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
